import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Autocomplete,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PersonIcon from '@mui/icons-material/Person';

const ChangeGuardDialog = ({
  open,
  patrolCount,
  currentGuardId,
  currentGuardName,
  guards,
  selectedGuard,
  onGuardSelect,
  onCancel,
  onConfirm,
}) => {
  // Filter out the current guard from options
  const availableGuards = guards.filter((g) => g.id !== currentGuardId);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Change Guard for Shift
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Select a new guard to assign to all <strong>{patrolCount} patrol{patrolCount !== 1 ? 's' : ''}</strong> in this shift.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <PersonIcon sx={{ color: 'action.active', mt: 2 }} />
          <Autocomplete
            value={selectedGuard}
            onChange={(_, newValue) => onGuardSelect(newValue)}
            options={availableGuards}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="New Guard"
                placeholder="Search guards..."
                fullWidth
              />
            )}
            sx={{ flex: 1 }}
          />
        </Box>
        {currentGuardName && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Current guard: <strong>{currentGuardName}</strong>
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          variant="contained"
          disabled={!selectedGuard}
        >
          Change Guard
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ChangeGuardDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  patrolCount: PropTypes.number.isRequired,
  currentGuardId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentGuardName: PropTypes.string,
  guards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedGuard: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  onGuardSelect: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default memo(ChangeGuardDialog);
